import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { SITE_URL, SITE_NAME, SITE_LOGO } from '@/constants/seo';

function buildArticleJsonLd(article: (typeof articles)[number]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: '2026-06-01',
    dateModified: '2026-06-29',
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: SITE_LOGO,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/article/${article.slug}`,
    },
  };
}

function buildFaqJsonLd(article: (typeof articles)[number]) {
  if (!article.content.faq || article.content.faq.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.content.faq.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const faqJsonLd = buildFaqJsonLd(article);

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildArticleJsonLd(article)) }}
      />
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}
      <Navbar />

      <article className="pt-28 pb-16 md:pb-24">
        <div className="max-w-[860px] mx-auto px-4 md:px-6">
          <header className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
              <Link
                to="/shopping-guide"
                className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
              >
                All Guides
              </Link>
            </div>

            <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-semibold tracking-wide mb-4">
              {article.category}
            </div>

            <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 mt-6 text-sm text-foreground-500">
              <span>Updated June 2026</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{article.readTime} read</span>
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>
          </header>

          <div className="space-y-10 text-foreground-800 leading-relaxed">
            <p className="text-lg text-foreground-700 leading-relaxed">
              {article.content.intro}
            </p>

            {article.content.sections.map((section, i) => (
              <section key={i}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, j) => (
                  <p key={j} className="text-base md:text-lg text-foreground-700 leading-relaxed mt-4">
                    {p}
                  </p>
                ))}
                {section.listItems && (
                  <ul className="list-disc pl-6 space-y-3 mt-4 text-base md:text-lg text-foreground-700">
                    {section.listItems.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                )}
                {section.table && (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b border-background-300">
                          {section.table.headers.map((h, j) => (
                            <th key={j} className="text-left py-3 pr-4 font-semibold text-foreground-900">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.rows.map((row, j) => (
                          <tr key={j} className="border-b border-background-200/60">
                            {row.map((cell, k) => (
                              <td key={k} className="py-3 pr-4 text-foreground-600">{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}

            {article.content.faq && article.content.faq.length > 0 && (
              <section>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-6">
                  {article.content.faq.map((faq, i) => (
                    <div key={i}>
                      <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2">
                        {faq.question}
                      </h3>
                      <p className="text-base text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200/50">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                Ready to Start Exploring?
              </h2>
              <p className="text-base text-foreground-700 leading-relaxed mb-6">
                {article.content.conclusion}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-white rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
                >
                  Back to Homepage
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/shopping-guide"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-300 rounded-full text-sm font-semibold text-foreground-700 hover:border-foreground-700 transition-all cursor-pointer whitespace-nowrap"
                >
                  More SEO Guides
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </section>

            <div className="pt-8 border-t border-background-200/40">
              <p className="text-sm text-foreground-500">
                This article is part of our SEO-optimized content series. For more curated fashion finds, visit our{' '}
                <Link to="/" className="text-accent-600 hover:text-accent-700 underline cursor-pointer">homepage</Link>{' '}
                or browse the{' '}
                <Link to="/shopping-guide" className="text-accent-600 hover:text-accent-700 underline cursor-pointer">Shopping Guide</Link>.
              </p>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}